import { RadioGroup as RadioGroupInput, Radio as RadioInput } from "@blueprintjs/core";
import { IInputProps } from "../types";
import { IModel, IMutators, IRadio, IRadioOption } from "components/Template/types";
import { doDisabled, doHidden } from "components/Template/utils";
import Validate, { renderInfo } from "components/Template/Validate";
import Override from "components/Template/Override";
import ChangeHighlight from "components/Template/ChangeHighlight";
import "./Radio.scss";

export interface IRadioProps extends IInputProps {
  template: IRadio;
  groupNameSuffix?: string;
}

export interface IRadioOptionProps {
  template: IRadioOption;
  model: IModel;
  mutators: IMutators;
}

const Radio = (props: IRadioProps) => {
  const { template, model, mutators, metadata, listContext, groupNameSuffix } = props;
  const { getValue, handleChange } = mutators;

  const findOptionValue = (v: string | null) => template.options?.find((o) => o.label === v || String(o.value) === v)?.value || "";
  if (doHidden(props)) {
    if (!metadata.hiddenInput.includes(template.name)) {
      metadata.hiddenInput.push(template.name);
    }
    return null;
  } else {
    if (metadata.hiddenInput.includes(template.name)) {
      metadata.hiddenInput = metadata.hiddenInput.filter((i) => i === template.name);
    }
  }

  const disabled = doDisabled(props);

  const renderRadio = (option: IRadioOption) => (
    <RadioInput
      key={option.value}
      label={option.label}
      value={option.value}
      disabled={doDisabled({ template: option, model, mutators })}
    >
      {option.description ? renderInfo(option.description, model) : undefined}
    </RadioInput>
  );

  const baseGroupName = listContext ? `${template.name}_${listContext.index}` : template.name;
  const radioGroupName = groupNameSuffix ? `${baseGroupName}__${groupNameSuffix}` : baseGroupName;

  const selectedValue = getValue(template.name) as string | number;
  const storedValue = mutators.getStoredValue ? mutators.getStoredValue(template.name) : undefined;
  const hasChanged = storedValue !== undefined && storedValue !== selectedValue;

  const handleUndo = () => {
    if (storedValue !== undefined && handleChange) {
      handleChange(template.name, storedValue);
    }
  };

  const handleKeep = () => {
    if (mutators.setStoredValue) {
      mutators.setStoredValue(template.name, selectedValue);
    }
  };

  return (
    <Validate {...props} className="radio-group" showLabel showInfo>
      <ChangeHighlight
        changed={hasChanged}
        onUndo={hasChanged ? handleUndo : undefined}
        onKeep={hasChanged ? handleKeep : undefined}
        changePath={mutators.getPath(template.name)}
      >
        <div className="radio-container">
          <RadioGroupInput
            key={radioGroupName}
            selectedValue={selectedValue}
            name={radioGroupName}
            {...(handleChange
              ? {
                  onChange: (v) => handleChange(template.name, findOptionValue(v.currentTarget.value)),
                  disabled: disabled,
                }
              : {
                  onChange: () => { },
                  readOnly: true,
                })}
          >
            {template.options?.map((option) => renderRadio(option))}
          </RadioGroupInput>
          <Override {...props} />
        </div>
      </ChangeHighlight>
    </Validate>
  );
};

export default Radio;
